<template>
  <div class="login-screen">
    <div class="login-card">
      <h1 class="login-title">NUEVA CONTRASEÑA</h1>

      <!-- Validando el enlace -->
      <template v-if="checking">
        <p class="login-sub">Validando el enlace…</p>
      </template>

      <!-- Enlace inválido o vencido -->
      <template v-else-if="!valid && !done">
        <div class="login-error">
          El enlace no es válido o ha expirado. Solicita uno nuevo desde
          "¿Olvidaste tu contraseña?".
        </div>
        <a href="/login/" class="login-btn login-btn-link">VOLVER A INICIAR SESIÓN</a>
      </template>

      <!-- Contraseña actualizada -->
      <template v-else-if="done">
        <div class="login-success">{{ successMessage }}</div>
        <a href="/login/" class="login-btn login-btn-link">IR A INICIAR SESIÓN</a>
      </template>

      <!-- Formulario para definir la nueva contraseña -->
      <template v-else>
        <p class="login-sub">Ingresa y confirma tu nueva contraseña.</p>

        <div v-if="errorMessage" class="login-error">{{ errorMessage }}</div>

        <form class="login-form" @submit.prevent="handleSubmit">
          <input
            v-model="password"
            class="login-input"
            type="password"
            placeholder="Nueva contraseña"
            autocomplete="new-password"
          />
          <input
            v-model="confirmPassword"
            class="login-input"
            type="password"
            placeholder="Repite la contraseña"
            autocomplete="new-password"
          />
          <button type="submit" class="login-btn" :disabled="loading">
            {{ loading ? 'GUARDANDO…' : 'GUARDAR CONTRASEÑA' }}
          </button>
        </form>

        <a href="/login/" class="login-forgot">← Volver a iniciar sesión</a>
      </template>
    </div>
  </div>
</template>

<script>
import {
  validatePasswordResetToken,
  confirmPasswordReset,
} from "@/services/teatroApi";

export default {
  name: "PasswordResetConfirm",
  data() {
    return {
      uid: "",
      token: "",
      password: "",
      confirmPassword: "",
      checking: true,
      valid: false,
      done: false,
      loading: false,
      errorMessage: "",
      successMessage: "",
    };
  },
  async created() {
    this.parseUrl();
    await this.validate();
  },
  methods: {
    parseUrl() {
      // La URL es /nueva-clave/<uid>/<token>/
      const parts = window.location.pathname.split("/").filter(Boolean);
      const idx = parts.indexOf("nueva-clave");
      if (idx !== -1) {
        this.uid = parts[idx + 1] || "";
        this.token = parts[idx + 2] || "";
      }
    },
    async validate() {
      this.checking = true;
      try {
        if (!this.uid || !this.token) {
          this.valid = false;
          return;
        }
        const data = await validatePasswordResetToken(this.uid, this.token);
        this.valid = !!data.valid;
      } catch (e) {
        this.valid = false;
        console.error(e);
      } finally {
        this.checking = false;
      }
    },
    async handleSubmit() {
      this.errorMessage = "";
      if (!this.password || this.password.length < 6) {
        this.errorMessage = "La contraseña debe tener al menos 6 caracteres.";
        return;
      }
      if (this.password !== this.confirmPassword) {
        this.errorMessage = "Las contraseñas no coinciden.";
        return;
      }
      this.loading = true;
      try {
        const data = await confirmPasswordReset(this.uid, this.token, this.password);
        this.done = true;
        this.successMessage =
          data.message || "Tu contraseña fue actualizada. Ya puedes iniciar sesión.";
      } catch (error) {
        this.errorMessage =
          error.response?.data?.error ||
          "No pudimos actualizar la contraseña. Solicita un nuevo enlace.";
        // Si el token venció entre la carga y el envío, mostrar el estado inválido.
        if (error.response?.status === 400) {
          this.valid = false;
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.login-screen {
  min-height: calc(100vh - 40px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
}

.login-card {
  width: 100%;
  max-width: 420px;
  background: rgba(10, 6, 6, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 22px;
  padding: 40px 38px 34px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6);
  text-align: center;
  backdrop-filter: blur(2px);
}

.login-title {
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 2px;
  margin: 0 0 8px;
}

.login-sub {
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  letter-spacing: 0.4px;
  margin: 0 0 26px;
}

.login-error {
  background: rgba(200, 16, 46, 0.15);
  border: 1px solid rgba(200, 16, 46, 0.5);
  color: #ffb3b3;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 12px;
  line-height: 1.5;
  margin-bottom: 16px;
}

.login-success {
  background: rgba(46, 160, 87, 0.15);
  border: 1px solid rgba(46, 160, 87, 0.5);
  color: #a7e8bf;
  border-radius: 8px;
  padding: 11px 14px;
  font-size: 12.5px;
  line-height: 1.5;
  margin-bottom: 16px;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.login-input {
  width: 100%;
  background: #fff;
  border: none;
  border-radius: 8px;
  padding: 13px 16px;
  font-family: var(--font-family-primary);
  font-size: 13px;
  color: #1a1a1a;
  outline: none;
}

.login-input::placeholder {
  color: #9a9a9a;
}

.login-btn {
  margin-top: 6px;
  width: 100%;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 13px;
  font-family: var(--font-family-heading);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(200, 16, 46, 0.4);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.login-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(200, 16, 46, 0.5);
}

.login-btn:disabled {
  opacity: 0.6;
  cursor: default;
  transform: none;
  box-shadow: none;
}

.login-btn-link {
  display: inline-block;
  text-decoration: none;
  text-align: center;
}

.login-forgot {
  display: inline-block;
  margin-top: 18px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 11px;
  text-decoration: none;
  transition: color 0.2s ease;
}

.login-forgot:hover {
  color: #fff;
}
</style>
